/**
 * Fence-drawing hit math, pure and pinned by test/fence-hit.test.ts.
 *
 * Defect history (2026-10-23, diagnosed from rendered edge IDs):
 * 1. The pointer was mapped with W x H while the SVG spans a
 *    "-1 -1 W+2 H+2" viewBox - a ~0.7% skew, up to ~2 m of hit error at the
 *    far edge of a 29-col parcel (stray edge "10,5,N").
 * 2. Score-based hysteresis let sub-pixel jitter at a vertex flip a locked
 *    straight drag onto a perpendicular edge (stray edge "8,2,W").
 * 3. Boundary points (finger on the parcel edge) failed eligibility on the
 *    parallel index and fell through to the perpendicular orientation -
 *    a stray at best, a dropped stroke at worst. The parallel index is now
 *    CLAMPED into range: tracing along the border paints the border edge.
 * The orientation lock is strict for the drag's duration; the lock itself
 * comes from the caller (FenceDrawing locks by travel axis after ~0.3 cell
 * of movement, or immediately for an unambiguous mid-edge first touch).
 */

/** Client point -> grid-meter point, viewBox-true ("-1 -1 W+2 H+2"). */
export function viewBoxPoint(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }, W: number, H: number): { px: number; py: number } {
  return {
    px: ((clientX - rect.left) / rect.width) * (W + 2) - 1,
    py: ((clientY - rect.top) / rect.height) * (H + 2) - 1,
  };
}

export type DragDir = 'N' | 'W' | null;

export interface EdgeCandidates {
  /** Canonical "x,y,N" key when a horizontal edge is within reach, else null. */
  n: string | null;
  /** Canonical "x,y,W" key when a vertical edge is within reach, else null. */
  w: string | null;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

/**
 * Both orientations' nearest edges to a grid-meter point, within 45% of a
 * cell on the PERPENDICULAR axis; the PARALLEL (cell) index is clamped into
 * range so boundary points hit the boundary edge instead of dropping out.
 */
export function edgeCandidates(px: number, py: number, cols: number, rows: number, edgeM: number): EdgeCandidates {
  const tol = edgeM * 0.45;
  const nyN = Math.round(py / edgeM);
  const dN = Math.abs(py - nyN * edgeM);
  const n = nyN >= 0 && nyN <= rows && dN <= tol
    ? `${String(clamp(Math.floor(px / edgeM), 0, cols - 1))},${String(nyN)},N`
    : null;
  const nxW = Math.round(px / edgeM);
  const dW = Math.abs(px - nxW * edgeM);
  const w = nxW >= 0 && nxW <= cols && dW <= tol
    ? `${String(nxW)},${String(clamp(Math.floor(py / edgeM), 0, rows - 1))},W`
    : null;
  return { n, w };
}

/**
 * Choose the edge to paint given the drag's locked orientation: the locked
 * orientation wins whenever it has a candidate; otherwise the other
 * orientation (which becomes the new lock). Unlocked: the nearer
 * orientation; null when neither is within reach. Ambiguous unlocked points
 * (both candidates live) prefer N on an exact tie - the caller holds back
 * painting near-vertex starts until travel shows the axis instead.
 */
export function pickEdge(px: number, py: number, cols: number, rows: number, edgeM: number, pref: DragDir): { edge: string; dir: 'N' | 'W' } | null {
  const { n, w } = edgeCandidates(px, py, cols, rows, edgeM);
  if (pref === 'N') {
    if (n !== null) return { edge: n, dir: 'N' };
    if (w !== null) return { edge: w, dir: 'W' };
    return null;
  }
  if (pref === 'W') {
    if (w !== null) return { edge: w, dir: 'W' };
    if (n !== null) return { edge: n, dir: 'N' };
    return null;
  }
  // Unlocked: paint only an UNAMBIGUOUS first touch (exactly one candidate).
  // Both candidates = near a vertex; the caller defers to the travel axis
  // rather than letting the first touch lock perpendicular to the finger.
  if (n !== null && w === null) return { edge: n, dir: 'N' };
  if (w !== null && n === null) return { edge: w, dir: 'W' };
  return null;
}
